import { useState } from 'react';
import { Link } from 'react-router';
import { useSearchParams } from 'react-router';
import { EmptyState, InlineError, Panel, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { PanelFold } from '../listView/PanelFold';
import { formatDate } from '../listView/TaskTable';
import {
  buildBoard,
  localIsoDate,
  isOverdue,
  RECORD_TYPE_LABELS,
  recordHref,
  SALES_TASK_TABS,
  TASK_TYPE_LABELS,
} from './salesTasksLens';
import type { SalesTask, SalesTaskTab } from './salesTasksLens';
import { salesTaskErrorMessage, useSalesTasks, useSetSalesTaskCompleted } from './queries';
import { SalesTaskDialog } from './SalesTaskDialog';
import './salesTasks.css';

/**
 * Sales Tasks: every task about a lead, company, deal or prospect, in one list.
 *
 * ── Why this page exists ───────────────────────────────────────────────────
 *
 * Kyu, 14 September: "All tasks about a lead, or a company or a deal will be
 * homed here. Regardless of who's it assigned to. So I can see Nez's and Ced's
 * task too, we can all see each other's tasks. It's like the Sales Team's task
 * in one place."
 *
 * Before this, sales work lived in two task tables and neither told you about
 * the other. A task created in the Sales Tracker did not reach My Tasks, did
 * not reach My Day, and notified nobody when it was assigned or went overdue —
 * measured by reading each of those features. 20270146000100 merged them, and
 * this page is what the merge was for.
 *
 * ── Everyone's tasks, and everyone can act ─────────────────────────────────
 *
 * There is no "mine" filter and that is the point. The database notifies the
 * owner when somebody else completes their task, so the shared list does not
 * need a rule asking people to remember to say so.
 *
 * ── Two click targets, on purpose ──────────────────────────────────────────
 *
 * The task title opens the task in a dialog over this list. The record name
 * beside it opens the deal, lead or company. Kyu, 15 September: "Both, split
 * by column." See SalesTaskDialog for why his spec needed that reading.
 *
 * ── What it does not do ────────────────────────────────────────────────────
 *
 * The record still opens on its own screen rather than stacking a drawer over
 * this one. Mounting DealDrawer here needs the deal's pipeline plus three
 * pipeline-scoped lists, which is its own step rather than a line of this one.
 */

const TITLE = 'Sales Tasks';

/**
 * One row of the table.
 *
 * A table, not a stacked list. The first version of this page put the title
 * and the record on two lines inside one cell and had no header row at all;
 * Kyu, looking at it beside HubSpot: "the UI and design sucks btw, not like
 * this." He was right. Forty tasks is something you scan down a column, and a
 * column only exists if every row puts the same thing in the same place.
 *
 * It renders through `.tracker-table`, which is what Leads, Companies, Lists,
 * CG Orders and the Supplier Exchange already use — so this page is on the
 * same beat as every other dense list in the Hub rather than being a fifth
 * answer to a question already settled.
 */
function TaskRow({
  task,
  today,
  onToggle,
  onOpen,
  busy,
}: {
  task: SalesTask;
  today: string;
  onToggle: (task: SalesTask, isCompleted: boolean) => void;
  onOpen: (task: SalesTask) => void;
  busy: boolean;
}) {
  const late = isOverdue(task, today);

  return (
    <tr className={task.is_completed ? 'sales-task-row--done' : undefined}>
      <td className="sales-task-tick">
        <input
          type="checkbox"
          className="task-check"
          checked={task.is_completed}
          disabled={busy}
          aria-label={task.is_completed ? `Reopen ${task.title}` : `Complete ${task.title}`}
          onChange={() => onToggle(task, !task.is_completed)}
        />
      </td>

      {/* A <td>, not a <th scope="row">, and deliberately.
          `.tracker-table th` in components.css is written for the header row:
          it is sticky, uppercase, and wears the card's fill. Those apply to
          every <th> in the table, header row or not, so a row header here
          would pin itself to the top of the pane and drop out of the zebra,
          whose selector only names <td>. Semantics are worth having but not
          at the cost of fighting a shared selector three agents deep in the
          week; the link carries the row's name either way. */}
      <td className="sales-task-title-cell">
        {/* The task half of the split. Kyu, 15 September, choosing how a row
            opens: "Both, split by column." The title opens the task; the
            record name in the next cell opens the record. That is what the
            HubSpot layout he asked us to follow already does, and it is the
            only reading that satisfies both halves of his spec — "dialog
            opening the record profile" and "Task Rescheduling & Options".

            A button, not a link: the dialog is state on this page, not an
            address. Drop 5 may give it one; until then a link would promise a
            URL that does not exist. */}
        <button
          type="button"
          className="sales-task-title"
          title={task.title}
          onClick={() => onOpen(task)}
        >
          {task.title}
        </button>
      </td>

      <td className="sales-task-about-cell">
        {/* The record's own name where there is one, and what kind of thing it
            is where there is not. A row that says only "Lead" is still more
            use than a row that says nothing. */}
        <Link to={recordHref(task)} className="sales-task-record" title={task.record_name ?? ''}>
          {task.record_name ?? RECORD_TYPE_LABELS[task.record_type]}
        </Link>
      </td>

      <td className="sales-task-kind">{RECORD_TYPE_LABELS[task.record_type]}</td>

      <td className="sales-task-type">
        {task.task_type === null ? TASK_TYPE_LABELS.todo : TASK_TYPE_LABELS[task.task_type]}
      </td>

      <td className="sales-task-assignee">{task.assignee_name ?? 'Unassigned'}</td>

      <td className={late ? 'sales-task-due sales-task-due--overdue' : 'sales-task-due'}>
        {task.due_date === null ? 'No due date' : formatDate(task.due_date)}
        {/* A mark, not a word. The colour does the telling and the symbol
            carries it for anybody who cannot see the colour. */}
        {late ? <span aria-label="Overdue"> ⚠</span> : null}
      </td>
    </tr>
  );
}

/* The column widths, which under `table-layout: fixed` can only be read from
   here or from the first row. See the long note in salesTasks.css: the widths
   used to sit on the tbody cells, where the browser never looked at them. */
function TaskTableCols() {
  return (
    <colgroup>
      <col className="sales-task-col-tick" />
      {/* Task deliberately has no width: it is the column that takes the
          slack, so the title gets every pixel the others do not need. */}
      <col />
      <col className="sales-task-col-about" />
      <col className="sales-task-col-kind" />
      <col className="sales-task-col-type" />
      <col className="sales-task-col-assignee" />
      <col className="sales-task-col-due" />
    </colgroup>
  );
}

/** The header row, shared by the open list and the completed fold. */
function TaskTableHead() {
  return (
    <thead>
      <tr>
        <th aria-label="Done" />
        <th>Task</th>
        <th>About</th>
        <th>Record</th>
        <th>Type</th>
        <th>Assigned</th>
        {/* The timezone is named because the team is in Manila and the dates
            are calendar days, not instants. HubSpot names it too, and for the
            same reason: a date column with no zone is a date somebody will
            eventually read wrong. */}
        <th>Due (GMT+8)</th>
      </tr>
    </thead>
  );
}

export function SalesTasksPage() {
  const [searchParams] = useSearchParams();
  const tasks = useSalesTasks();
  const setCompleted = useSetSalesTaskCompleted();
  const [tab, setTab] = useState<SalesTaskTab>('all');
  const [error, setError] = useState('');
  // Held by id, not by object: the list refetches after every edit, and a
  // captured row would go stale the moment the dialog saved anything.
  const [openTaskId, setOpenTaskId] = useState<string | null>(() => searchParams.get('task'));

  // Read once per render rather than per row, so every row on one screen
  // agrees about what day it is.
  const today = localIsoDate(new Date());

  function toggle(task: SalesTask, isCompleted: boolean) {
    setError('');
    setCompleted.mutate(
      { taskId: task.id, isCompleted },
      { onError: (cause: unknown) => setError(salesTaskErrorMessage(cause)) },
    );
  }

  if (tasks.isPending) {
    return (
      <>
        <PageHeader title={TITLE} topbar />
        <Skeleton lines={8} label="Loading sales tasks" />
      </>
    );
  }

  if (tasks.isError) {
    return (
      <>
        <PageHeader title={TITLE} topbar />
        <Panel>
          <InlineError>{salesTaskErrorMessage(tasks.error)}</InlineError>
        </Panel>
      </>
    );
  }

  const board = buildBoard(tasks.data ?? [], tab, today);

  // Resolved against the freshly loaded rows every render, which is why the
  // state above holds an id. Open, done and gone are all reachable: a task
  // completed inside the dialog moves from board.open to board.done, and the
  // dialog has to follow it rather than vanish mid-edit.
  const openTask =
    openTaskId === null
      ? null
      : ([...board.open, ...board.done].find((row) => row.id === openTaskId) ?? null);

  return (
    <>
      <PageHeader title={TITLE} topbar />

      <div className="sales-tasks-page">
        {error === '' ? null : <InlineError>{error}</InlineError>}

        <div
          className="sales-tasks-tabs"
          role="group"
          aria-label="Filter tasks by when they are due"
        >
          {SALES_TASK_TABS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className="view-toggle-button"
              aria-pressed={tab === entry.key}
              onClick={() => setTab(entry.key)}
            >
              {entry.label}
              {/* Only the Overdue tab carries a count. The others are
                  navigation; this one is news, and it is the number somebody
                  needs before they decide which tab to open. */}
              {entry.key === 'overdue' && board.overdueCount > 0 ? (
                <span className="sales-tasks-tab-count">{board.overdueCount}</span>
              ) : null}
            </button>
          ))}
        </div>

        {board.open.length === 0 ? (
          <EmptyState
            title={tab === 'all' ? 'No sales tasks yet' : 'Nothing in this view'}
            description={
              tab === 'all'
                ? 'Tasks created against a lead, company, deal or prospect appear here, whoever they are assigned to.'
                : 'Try All tasks to see everything the team has open.'
            }
          />
        ) : (
          <div className="tracker-table-scroll">
            <table className="tracker-table sales-tasks-table">
              <TaskTableCols />
              <TaskTableHead />
              <tbody>
                {board.open.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    today={today}
                    onToggle={toggle}
                    onOpen={(row) => setOpenTaskId(row.id)}
                    busy={setCompleted.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Folded, at the bottom, holding everything finished regardless of
            which tab is open. Kyu: "I kind of like how tasks are done in the
            My Day. There's a collapsed section where you can see all completed
            tasks." Same PanelFold component My Day uses. */}
        {board.done.length === 0 ? null : (
          <div className="sales-tasks-done">
            <PanelFold title="Completed" summary={String(board.done.length)} defaultOpen={false}>
              <div className="tracker-table-scroll">
                <table className="tracker-table sales-tasks-table">
                  <TaskTableCols />
                  <TaskTableHead />
                  <tbody>
                    {board.done.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        today={today}
                        onToggle={toggle}
                        onOpen={(row) => setOpenTaskId(row.id)}
                        busy={setCompleted.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </PanelFold>
          </div>
        )}
      </div>

      {openTask === null ? null : (
        // Keyed by id so switching tasks with the dialog open rebuilds the
        // local title state instead of showing the previous task's text. Same
        // reason ListView and My Day key their panel.
        <SalesTaskDialog key={openTask.id} task={openTask} onClose={() => setOpenTaskId(null)} />
      )}
    </>
  );
}
